import {
  AbstractControl,
  AbstractControlOptions,
  NonNullableFormBuilder,
} from '@angular/forms';
import { Type } from '@angular/core';
import { getInstanceOrClassMetadata } from '../../metadata';
import { FilterOperator } from '../../../api/api.operators';
import { ApiResource } from '../../../api/api-resource';
import {
  booleanFilters,
  dateFilters,
  dateNotFilters,
  numberFilters,
  numberNotFilters,
  stringFilters,
} from '../../filters/filter.util';
import { BlobLocationFormControl } from '../../forms/image-upload/blob-location-form-control';

export interface FieldConfig {
  label?: string;
  type?: () => any;
  readonly?: boolean;
  default?: () => any;
  formControlFactory?: (fb: NonNullableFormBuilder) => AbstractControlOptions;
  includeFilter?: boolean;
  operators?: FilterOperator[];
  notOperators?: FilterOperator[];
}

export interface FieldData {
  key: string | symbol;
  label: string;
  type: () => any;
  operators?: FilterOperator[];
  notOperators?: FilterOperator[];
  readonly?: boolean;
  default?: any;
  component?: Type<any>;
  array?: boolean;
  formControlFactory?: (fb: NonNullableFormBuilder) => AbstractControlOptions;
  includeFilter?: boolean;
}

export const FIELDS = Symbol('ResourceField');
export const Field =
  (config: FieldConfig = {}): PropertyDecorator =>
  (target, propertyKey) => {
    validateFieldConfig(config);

    const fields = {
      ...(Reflect.getMetadata(FIELDS, target.constructor) ?? {}),
    };
    fields[propertyKey] = {
      key: propertyKey,
      label: config.label ?? propertyKey,
      type: createFieldTypeResolver(config, target, propertyKey),
      readonly: config.readonly ?? false,
      default: config.default ? config.default() : undefined,
      array: Reflect.getMetadata('design:type', target, propertyKey) === Array,
      formControlFactory: config.formControlFactory,
      includeFilter: config.includeFilter ?? true,
    };
    Reflect.defineMetadata(FIELDS, fields, target.constructor);
  };

function createFieldTypeResolver(
  config: FieldConfig,
  target: Object,
  propertyKey: string | symbol,
) {
  return () => {
    const type =
      (config.type ?? (() => null))() ??
      Reflect.getMetadata('design:type', target, propertyKey);
    const errorMessage = `Could not establish proper type of '${target.constructor.name}.${String(propertyKey)}' column. Received: ${type}. `;
    if (!type) throw new Error(errorMessage);
    if (type == Array)
      throw new Error(
        errorMessage +
          `If type is an array, please provide the type of the array elements.`,
      );
    return type;
  };
}

export function validateFieldConfig(config: FieldConfig): void {
  if (
    config.default &&
    (typeof config.default !== 'function' ||
      config.default.prototype !== undefined)
  ) {
    throw new Error(
      'Default should be a function which returns the default value',
    );
  }

  if (
    config.type &&
    (typeof config.type !== 'function' || config.type.prototype !== undefined)
  ) {
    throw new Error('Type should be a function which returns the type value');
  }
}

export function getFieldsArray(resource: unknown): FieldData[] {
  return Object.values(getFields(resource));
}

export function getFields<
  T extends unknown,
  K = T extends Type<infer K> ? K : T,
>(resource: T): { [k in keyof K]: FieldData } {
  return getInstanceOrClassMetadata(resource, FIELDS) ?? {};
}

export function getFieldsAsForm<
  T extends unknown,
  K = T extends Type<infer K> ? K : T,
>(resource: T, fb: NonNullableFormBuilder) {
  const group = fb.group(
    Object.fromEntries(
      Object.entries(getFields(resource))
        .filter(([key, fieldData]) => !fieldData.readonly)
        .map(([key, fieldData]) => {
          const formControlFactory = fieldData.formControlFactory;
          if (!formControlFactory) {
            return [
              key,
              [{ value: fieldData.default ?? undefined, disabled: false }],
            ];
          }

          return [key, formControlFactory(fb)];
        }),
    ) as { [k in keyof K]: AbstractControl<K[k]> },
  );
  return group;
}

export function getNestedFields(
  resource: Type<ApiResource<any>>,
  maxDepth: number,
) {
  const getSubFields = (resource: Type<ApiResource<any>>) =>
    getFieldsArray(resource).filter((f) => f.includeFilter ?? true);

  const fields = getSubFields(resource);
  let newFields = fields;
  let depth = 0;
  while (depth++ < maxDepth) {
    newFields = newFields.flatMap((f) =>
      getSubFields(f.type()).map((innerField) => ({
        ...innerField,
        key: `${f.key.toString()}.${innerField.key.toString()}`,
        label: `${f.label} > ${innerField.label}`,
      })),
    );
    fields.push(...newFields);
  }
  return fields;
}

export function getNestedFieldData(
  resource: Type<ApiResource<any>>,
  name: string,
): FieldData {
  let fields = getFields(resource);
  const paths = name.split('.');
  if (paths.length == 1) {
    if (!(name in fields)) {
      throw new Error(`Column '${name}' not found on ${resource.name}`);
    }
    return fields[name as keyof typeof fields];
  }
  let field!: FieldData;
  for (const [i, path] of paths.entries()) {
    if (!(path in fields)) {
      throw new Error(
        `Column '${path}' (part of ${name}) not found on ${resource.name}`,
      );
    } else {
      field = fields[path as keyof typeof fields];
      if (i == paths.length - 1) break;
      resource = field.type();
      fields = getFields(resource);
      if (fields === undefined) {
        throw new Error(
          `Can't recurse into column '${path}' (part of ${name})`,
        );
      }
    }
  }
  // Override the key so that derived filter data links to correct filter column
  return { ...field, key: name };
}

export const StringField = (config: FieldConfig = {}) =>
  Field({
    operators: stringFilters(),
    notOperators: stringFilters(),
    ...config,
  });

export const DateField = (config: FieldConfig = {}) =>
  Field({
    operators: dateFilters(),
    notOperators: dateNotFilters(),
    ...config,
  });

export const NumberField = (config: FieldConfig = {}) =>
  Field({
    operators: numberFilters(),
    notOperators: numberNotFilters(),
    ...config,
  });

export const BooleanField = (config: FieldConfig = {}) =>
  Field({
    operators: booleanFilters(),
    notOperators: booleanFilters(),
    ...config,
  });

export const BlobField = (config: FieldConfig = {}) =>
  Field({
    type: () => Blob,
    includeFilter: false,
    formControlFactory: () => new BlobLocationFormControl(null),
    ...config,
  });

export const CoordinatesField = (config: FieldConfig = {}) =>
  Field({
    includeFilter: false,
    ...config,
  });
